import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { addonsAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Addon } from '../../types';

interface AddonFormData {
  addonName: string;
  description: string;
  hasOptions: boolean;
  displayOrder: number;
  isActive: boolean;
}

const Addons = () => {
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAddon, setEditingAddon] = useState<Addon | null>(null);
  const [formData, setFormData] = useState<AddonFormData>({ addonName: '', description: '', hasOptions: false, displayOrder: 0, isActive: true });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchAddons(); }, []);

  const fetchAddons = async () => {
    try { const response = await addonsAPI.getAll(); setAddons(response.data); setError(''); }
    catch (err) { setError('Failed to load addons'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (addon: Addon | null = null) => {
    if (addon) {
      setEditingAddon(addon);
      setFormData({ addonName: addon.AddonName, description: addon.Description || '', hasOptions: !!addon.HasOptions, displayOrder: addon.DisplayOrder, isActive: !!addon.IsActive });
    } else {
      setEditingAddon(null);
      setFormData({ addonName: '', description: '', hasOptions: false, displayOrder: 0, isActive: true });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingAddon(null); setFormData({ addonName: '', description: '', hasOptions: false, displayOrder: 0, isActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingAddon) { await addonsAPI.update(editingAddon.AddonId, formData); }
      else { await addonsAPI.create(formData); }
      fetchAddons(); handleCloseDialog(); setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save addon');
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await addonsAPI.delete(id); fetchAddons(); setError(''); } catch (err) { setError('Failed to delete addon'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Addons</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>Add Addon</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Addon Name</TableCell><TableCell>Description</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {addons.map((addon) => (
              <TableRow key={addon.AddonId}>
                <TableCell>{addon.AddonName}</TableCell><TableCell>{addon.Description || '-'}</TableCell>
                <TableCell>{addon.DisplayOrder}</TableCell><TableCell>{addon.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(addon)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(addon.AddonId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAddon ? 'Edit Addon' : 'Add Addon'}</DialogTitle>
        <DialogContent>
          <TextField size="small" autoFocus margin="dense" label="Addon Name" fullWidth value={formData.addonName} onChange={(e) => setFormData({ ...formData, addonName: e.target.value })} required />
          <TextField size="small" margin="dense" label="Description" fullWidth multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <TextField size="small" margin="dense" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.addonName}>{editingAddon ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Addon"
        message="Are you sure you want to deactivate this addon?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default Addons;
